import React, { Component } from 'react'
import { Alert, Modal } from 'antd'
import { injectIntl, FormattedMessage } from 'react-intl'
import { withApollo } from '@apollo/client/react/hoc'
import gql from 'graphql-tag'
import isEmpty from 'lodash/isEmpty'
import keyBy from 'lodash/keyBy'

import Tracks from '../tracks/Tracks'
import Blame from '../../components/Blame'
import Loading from '../../components/LoadingPage'
import * as Constants from '../../components/Constants'
import * as Utils from '../../components/Utils'

const BlacklistPlaylistsQuery = gql`
	query fetchBlacklistPlaylists($deviceId: MongoID!) {
		device: deviceById(_id: $deviceId) {
			blacklistedTracksWithPlaylist {
				trackId
				playlists
			}
		}
	}
`

/**
 * This component display all the blacklisted tracks for the device.
 * On load it fetch the extended blacklisted tracks from a custom field
 *
 * Fetching the playlist tracklist is heavy, that why there is a dedicated component
 * for this so it's only done when the modal is opened. The data is not cached in Apollo store
 * to ensure it's always up to date when the modal is opened.
 */
class BlacklistModal extends Component {
	state = {
		playlistsByTrack: null,
		error: null
	}

	componentDidMount() {
		this.loadPlaylists()
	}

	componentWillUnmount() {
		this.unmounted = true
	}

	loadPlaylists = async () => {
		const { client, device } = this.props
		try {
			const result = await client.query({
				query: BlacklistPlaylistsQuery,
				variables: { deviceId: device._id },
				// no cache, since there is no use case to keep all the playlist in memory
				// user are not supposed to open the blacklist modal repeatedly
				fetchPolicy: 'no-cache'
			})
			if (this.unmounted) {
				return
			}
			this.setState({
				playlistsByTrack: keyBy(
					result.data.device.blacklistedTracksWithPlaylist,
					'trackId'
				),
				error: null
			})
		} catch (e) {
			if (!this.unmounted) {
				this.setState({ error: e })
			}
		}
	}

	subtitle = (track) => {
		const { device } = this.props
		const playlists = this.state.playlistsByTrack[track._id]?.playlists

		return (
			<React.Fragment>
				{!isEmpty(playlists) ?
					<FormattedMessage
						id="devices.details.blacklist.playlists"
						values={{ playlists: [...playlists].join(', ') }}
					/>
				:	<FormattedMessage id="devices.details.blacklist.noPlaylist" />}
				<br />
				<Blame
					dateTitle="devices.details.blacklist.blame.date"
					dateUserTitle="devices.details.blacklist.blame.dateUser"
					eventFilters={{
						deviceUuid: device.uuid,
						trackId: track._id,
						event: Constants.EVENT_BLACKLIST
					}}
				/>
			</React.Fragment>
		)
	}

	renderContent() {
		const { device, intl } = this.props
		const { playlistsByTrack, error } = this.state

		if (error) {
			return (
				<Alert
					message={intl.formatMessage({ id: 'error' })}
					description={Utils.displayGraphQLErrors(error)}
					type="error"
					showIcon
				/>
			)
		}

		if (!playlistsByTrack) {
			return <Loading />
		}

		return (
			<Tracks
				tracks={device.blacklist}
				device={device}
				canBlacklist
				subtitle={this.subtitle}
			/>
		)
	}

	render() {
		const { device, intl, onClose } = this.props

		return (
			<Modal
				title={intl.formatMessage(
					{ id: 'devices.details.blacklist' },
					{ count: device.blacklist.length }
				)}
				visible={true}
				footer={[]}
				onCancel={onClose}
			>
				{this.renderContent()}
			</Modal>
		)
	}
}

export default withApollo(injectIntl(BlacklistModal))
